import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OffersBlock from "@/components/seo/OffersBlock";
import RecentPagesBlock from "@/components/seo/RecentPagesBlock";
import PopularPagesBlock from "@/components/seo/PopularPagesBlock";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Trophy, Zap, TrendingUp } from "lucide-react";
import heroFootball from "@/assets/hero-hub-football.webp";

const FootballHub = () => {
  const { data: matches } = useQuery({
    queryKey: ["football-matches-hub"],
    queryFn: async () => {
      const { data } = await supabase
        .from("football_matches")
        .select("*")
        .eq("published", true)
        .order("match_date", { ascending: true })
        .limit(200);
      return data || [];
    },
  });

  return (
    <>
      <Helmet>
        <title>Football en Streaming HD - Matchs en Direct | RoiDuStreaming</title>
        <meta name="description" content="Regardez tous les matchs de football en streaming HD et 4K. Ligue 1, Champions League, Premier League en direct sur RoiDuStreaming." />
        <link rel="canonical" href="https://roidustreaming.fr/football" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <section className="relative py-20 md:py-32 px-4 overflow-hidden">
          <img src={heroFootball} alt="Football en streaming HD en direct" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" />

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 text-secondary text-sm font-semibold mb-6">
              <TrendingUp className="w-4 h-4" />
              Tous les matchs en direct
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4 text-foreground leading-tight">
              Football en <span className="text-secondary">Streaming</span>
              <br />
              <span className="text-2xl md:text-4xl font-bold text-muted-foreground">HD & 4K • En direct • Toutes les compétitions</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Ligue 1, Champions League, Premier League, Liga, Serie A, Bundesliga… Tous les matchs en direct sur RoiDuStreaming.
            </p>
          </div>
        </section>

        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-foreground">Prochains matchs en streaming</h2>
            {matches && matches.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {matches.map((match) => (
                  <Link key={match.id} to={`/football/${match.slug}`} title={`${match.home_team} – ${match.away_team} streaming en direct`}>
                    <Card className="p-4 bg-card border-primary/20 hover:border-primary/50 transition-all hover:scale-[1.02]">
                      <p className="text-xs text-primary font-semibold uppercase mb-1">{match.competition}</p>
                      <h3 className="text-lg font-bold text-foreground mb-1">
                        {match.home_team} – {match.away_team} <span className="sr-only">streaming en direct</span>
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        📅 {new Date(match.match_date).toLocaleDateString("fr-FR")}
                        {match.match_time && ` à ${match.match_time}`}
                      </p>
                      {(match.image_url || (match as any).away_image_url) && (
                        <div className="flex items-center justify-center gap-4 mt-3">
                          {match.image_url && (
                            <img src={match.image_url} alt={match.home_team} className="h-12 w-12 object-contain" loading="lazy" />
                          )}
                          {match.image_url && (match as any).away_image_url && (
                            <span className="text-xs font-bold text-muted-foreground">VS</span>
                          )}
                          {(match as any).away_image_url && (
                            <img src={(match as any).away_image_url} alt={match.away_team} className="h-12 w-12 object-contain" loading="lazy" />
                          )}
                        </div>
                      )}
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-12">Aucun match programmé pour le moment. Revenez bientôt !</p>
            )}
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4">
          <OffersBlock />
        </div>

        <PopularPagesBlock />
        <RecentPagesBlock />

        <Footer />
      </div>
    </>
  );
};

export default FootballHub;
